import { forwardRef } from 'react';
import { format } from 'date-fns';
import {
  StarIcon,
  FolderPlusIcon,
  CheckIcon,
  TrashIcon,
  ShareIcon,
  BookOpenIcon,
  LanguageIcon,
  Squares2X2Icon,
  AcademicCapIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import Image from '../../components/Image';
import PremiumIconCrown from '../../components/PremiumIconCrown';
import { ThreeDotMenu } from '../Packet/Components/ThreeDotMenu';
import { useMyResourcesModals } from './Contexts/MyResourcesModalsContext';
import { useCollections } from './Contexts/CollectionsContext';
import { DELETE_TYPES } from './Modals/DeleteConfirmationModal';
import { Mixpanel } from '../../services/Mixpanel';
import { getPacketStandards, getPillNameFromSkillOrStandard } from '../../utils/skills';

export const PacketCardInner = forwardRef(
  (
    {
      packet,
      onToggleFavorite,
      onDeletePacket,
      onAddToCollection,
      onRemoveFromCollection,
      collectionFilters = [],
      onAddCollectionToFilters,
      onRemoveCollectionFromFilters,
      isAgeGated = false,
      selectMode = false,
    },
    ref
  ) => {
    const { setShowCreateOrRenameCollectionModal } = useMyResourcesModals();
    const isFavorite = packet.is_favorite;
    const toggleFavorite = async (e) => {
      e.stopPropagation();
      e.preventDefault();
      Mixpanel.track('Packet Favorite Toggled', {
        packet_id: packet.id,
        packet_title: packet.title,
        from: isFavorite,
        to: !isFavorite,
      });
      onToggleFavorite(packet.id, !isFavorite);
    };
    return (
      <a
        aria-label={`Resource: ${packet.title}`}
        tabIndex={0}
        href={`/p/${packet.id}`}
        ref={ref}
        onClick={() => {
          Mixpanel.track('Open from My Resources', {
            packet_id: packet.id,
            packet_title: packet.title,
          });
        }}
        className={`rounded-md p-4 flex flex-col gap-y-1 w-full ${
          selectMode ? '' : 'bg-white hover:bg-gray-100 cursor-pointer'
        }`}>
        <div className='flex flex-row justify-between items-center text-xs text-gray-500'>
          <div>{format(new Date(packet.timestamp), 'MMMM do, yyyy')}</div>
          <div className='flex flex-row items-center gap-x-1'>
            {isAgeGated && (
              <PremiumIconCrown className='h-3 w-3' wrapperClassName='flex items-center pr-1.5' />
            )}
            {/* These do nothing while the row is a checkbox, so don't offer them. */}
            {!selectMode && (
              <>
                <button
                  onClick={toggleFavorite}
                  aria-label={isFavorite ? 'Remove favorite' : 'Add favorite'}
                  className={`flex flex-row items-center gap-x-1 p-1.5 rounded-lg hover:bg-gray-200`}>
                  {isFavorite ? (
                    <StarIconSolid className={`w-4 h-4 text-emerald-600`} />
                  ) : (
                    <StarIcon className={`w-4 h-4 text-gray-500`} />
                  )}
                </button>
                <FolderDropdown
                  packet={packet}
                  collectionsOnPacket={packet.collections}
                  location='my_resources'
                  onAdd={(collection) => {
                    onAddToCollection(packet.id, collection);
                  }}
                  onRemove={(collection) => {
                    onRemoveFromCollection(packet.id, collection);
                  }}
                  onCreateCollection={() =>
                    setShowCreateOrRenameCollectionModal({
                      collection: null,
                      onCreate: (newCollection) => onAddToCollection(packet.id, newCollection),
                    })
                  }
                />

                <ThreeDotMenuPacketActions packet={packet} onDeletePacket={onDeletePacket} />
              </>
            )}
          </div>
        </div>
        <div className={`flex flex-row gap-x-1 ${packet.collections?.length ? '' : 'hidden'}`}>
          {[...new Set(packet.collections)].map((collection) => {
            const isPacketFiltered = collectionFilters.includes(collection.id);
            return (
              <button
                aria-label={`${isPacketFiltered ? 'Remove' : 'Add'} ${collection.title} to filter`}
                key={collection.id}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  Mixpanel.track('Collection Filter Toggled', {
                    location: 'packet_card',
                    packet_id: packet.id,
                    packet_title: packet.title,
                    collection_id: collection.id,
                    collection_title: collection.title,
                    from: isPacketFiltered,
                    to: !isPacketFiltered,
                  });
                  if (isPacketFiltered) {
                    onRemoveCollectionFromFilters(collection.id);
                  } else {
                    onAddCollectionToFilters(collection.id);
                  }
                }}
                className={`text-xs text-gray-600 font-medium px-2 py-0.5 rounded-md ${isPacketFiltered ? 'bg-emerald-50 hover:bg-emerald-100 border border-emerald-200' : 'bg-gray-50 hover:bg-gray-100 border border-gray-200'}`}>
                {collection.title}
              </button>
            );
          })}
        </div>
        <div className='text-lg text-gray-900 font-semibold hover:text-emerald-700 transition-colors'>
          {packet.title}
        </div>

        <PacketDetailRow packet={packet} />
      </a>
    );
  }
);

PacketCardInner.displayName = 'PacketCardInner';

export const PacketDetailRow = ({ packet, showTemplate = true }) => {
  const standards = getPacketStandards(packet);

  return (
    <div className='flex flex-row gap-x-2 items-center'>
      <PacketDetailIcon Icon={BookOpenIcon} text={packet.reading_level_name} />
      <PacketDetailIcon Icon={LanguageIcon} text={packet.language} />
      <PacketDetailIcon
        Icon={FormatIcon}
        packet={packet}
        text={packet.orientation === '16x9' ? 'Slides' : 'Pages'}
      />
      {packet.template_name && showTemplate && (
        <PacketDetailIcon Icon={Squares2X2Icon} text={packet.template_name} />
      )}

      {standards.length > 0 && (
        <PacketDetailIcon
          Icon={AcademicCapIcon}
          text={standards.map((standard) => getPillNameFromSkillOrStandard(standard)).join(', ')}
        />
      )}
    </div>
  );
};

const FormatIcon = ({ packet }) => {
  if (packet.orientation === '16x9') {
    return <Image src={`/homepage-slide-icon.svg`} className={`w-3 h-3 opacity-40`} alt='Slides' />;
  } else {
    return <Image src={`/homepage-doc-icon.svg`} className={`w-3 h-3 opacity-40`} alt='Pages' />;
  }
};

const PacketDetailIcon = ({ Icon, text, packet }) => {
  return (
    <div className='text-xs text-gray-500 flex flex-row items-center gap-x-0.5'>
      <Icon className='w-3 h-3' packet={packet} />
      <p className='text-xs text-gray-500 max-w-[190px] truncate'>{text}</p>
    </div>
  );
};

export const FolderDropdown = ({
  packet,
  direction = 'right',
  onAdd,
  onRemove,
  onCreateCollection,
  extraComponents,
  topComponents,
  ButtonIcon = FolderPlusIcon,
  location,
}) => {
  const { collections } = useCollections();

  const getIsPacketInCollection = (collection) => {
    return packet?.collections?.some((c) => c.id === collection.id);
  };

  const buttons = collections.map((collection) => {
    const isPacketInCollection = getIsPacketInCollection(collection);
    return (
      <button
        key={collection.id}
        className='w-full flex flex-row items-center gap-x-1.5 py-2 px-3 text-gray-700  hover:bg-gray-100'
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          if (isPacketInCollection) {
            Mixpanel.track('Packet Removed from Collection', {
              location: location,
              collection_id: collection.id,
              collection_title: collection.title,
              packet_id: packet.id,
              packet_title: packet.title,
            });
            onRemove(collection);
          } else {
            Mixpanel.track('Packet Added to Collection', {
              location: location,
              collection_id: collection.id,
              collection_title: collection.title,
              packet_id: packet.id,
              packet_title: packet.title,
            });
            onAdd(collection);
          }
        }}>
        {isPacketInCollection ? (
          <CheckIcon className='w-4 h-4 text-emerald-700' />
        ) : (
          <div className='w-4 h-4' />
        )}
        {collection.title}
      </button>
    );
  });
  if (extraComponents || topComponents || onCreateCollection) {
    // add a header to the front of the collections list
    buttons.unshift(
      <div
        key='add-to-collection-header'
        className='w-full flex flex-row items-center px-3 py-2 cursor-default text-xs hover:bg-white text-gray-700 font-medium'>
        Add to folder
      </div>
    );
    if (topComponents) {
      buttons.unshift(...topComponents);
    }
    if (extraComponents) {
      buttons.push(...extraComponents);
    }
  }

  if (onCreateCollection) {
    if (collections.length > 0) {
      buttons.push(
        <div key='create-collection-divider' className='h-px w-full my-1 bg-gray-200' />
      );
    }
    buttons.push(
      <button
        key='create-collection'
        className='w-full flex flex-row items-center gap-x-1.5 py-2 px-3 text-gray-700 hover:bg-gray-100'
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          Mixpanel.track('Add Collection Clicked', { location });
          onCreateCollection();
        }}>
        <PlusIcon className='w-4 h-4' />
        Create new folder
      </button>
    );
  }

  if (buttons.length === 0) {
    return null;
  }

  return (
    <div>
      <ThreeDotMenu
        mixpanelTrackEvent='Collection Folder Dropdown Clicked'
        location={location}
        direction={direction}
        buttonIcon={
          <ButtonIcon
            className={`w-4 h-4 ${location.includes('my_resources') ? 'text-gray-500' : 'text-gray-700'}`}
          />
        }
        buttonClassName='hover:bg-gray-200 p-1.5'
        aria-label='Collection Dropdown'
        buttons={buttons}
      />
    </div>
  );
};

const ThreeDotMenuPacketActions = ({ packet, onDeletePacket }) => {
  const { setShowDeleteConfirmationModal, setSharePacketModalOpen } = useMyResourcesModals();
  return (
    <div
      onClick={(e) => {
        e.stopPropagation();
      }}>
      <ThreeDotMenu
        direction='right'
        location='Packet Card'
        buttonClassName='hover:bg-gray-200 p-1.5'
        buttons={[
          <DropdownButton
            key='delete'
            label='Delete'
            aria-label='Delete Packet'
            icon={<TrashIcon className='w-4 h-4' />}
            onClick={() => {
              Mixpanel.track('Delete Packet Clicked', {
                packet_id: packet.id,
                packet_title: packet.title,
              });
              setShowDeleteConfirmationModal({
                type: DELETE_TYPES.PACKET,
                item: packet,
                onDelete: () => {
                  onDeletePacket(packet.id);
                },
              });
            }}
          />,
          <DropdownButton
            key='share'
            label='Share'
            aria-label='Share Packet'
            icon={<ShareIcon className='w-4 h-4' />}
            onClick={() => {
              Mixpanel.track('Share', {
                packet_id: packet.id,
                packet_title: packet.title,
              });
              setSharePacketModalOpen({ packet });
            }}
          />,
        ]}
      />
    </div>
  );
};

export const DropdownButton = ({ label, icon, onClick, ...buttonProps }) => {
  return (
    <button
      className='w-full flex flex-row items-center gap-x-1.5 py-2 px-3 text-gray-600  hover:bg-gray-100'
      {...buttonProps}
      onClick={onClick}>
      {icon} {label}
    </button>
  );
};
